/**
 * Third-party analytics and ad tags (Phase 20). Ids come from admin →
 * Tracking (GET /api/t/config); nothing loads until the visitor allows it:
 *
 *   analytics consent → PostHog, Google Analytics 4, Microsoft Clarity
 *   marketing consent → Meta Pixel, Google Ads, TikTok, LinkedIn, X
 *
 * Google's tag uses Consent Mode v2 (everything denied until the visitor
 * chooses). GA4 itself is in every page's HTML (components/GoogleTag.tsx) so
 * Google detects it; before consent it only gets cookieless, anonymous pings. Named events from lib/track.ts are forwarded to every loaded tag,
 * with the event ids the server-side conversions use, so nothing is counted
 * twice.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import { browserId, getConsent, type Consent } from "./track";

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL ?? "http://localhost:8000";

export interface PublicTracking {
  posthog_key?: string;
  posthog_host?: string;
  posthog_session_replay?: boolean;
  meta_pixel_id?: string;
  ga4_measurement_id?: string;
  google_ads_id?: string;
  google_ads_signup_label?: string;
  google_ads_purchase_label?: string;
  tiktok_pixel_id?: string;
  linkedin_partner_id?: string;
  linkedin_signup_conversion_id?: string;
  x_pixel_id?: string;
  x_signup_event_id?: string;
  clarity_project_id?: string;
  consent_banner?: boolean;
  record_clicks?: boolean;
}

export interface PublicConfig {
  tracking: PublicTracking;
  company: Record<string, string | number | null>;
  /** Pre-registration and maintenance (admin → Launch & maintenance). */
  access?: { prelaunch?: boolean; prelaunch_title?: string; prelaunch_message?: string } | null;
}

let configPromise: Promise<PublicConfig | null> | null = null;
const loaded = new Set<string>();

export function getConfig(): Promise<PublicConfig | null> {
  if (!configPromise) {
    configPromise = fetch(`${API_BASE}/api/t/config`)
      .then((r) => (r.ok ? (r.json() as Promise<PublicConfig>) : null))
      .catch(() => null);
  }
  return configPromise;
}

function w(): any {
  return window as any;
}

function script(id: string, src: string, attrs: Record<string, string> = {}) {
  if (document.getElementById(id)) return;
  const s = document.createElement("script");
  s.id = id;
  s.async = true;
  s.src = src;
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
  document.head.appendChild(s);
}

function gtagBase() {
  const win = w();
  if (win.gtag) return;
  win.dataLayer = win.dataLayer || [];
  win.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    win.dataLayer.push(arguments);
  };
  win.gtag("consent", "default", { ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied", analytics_storage: "denied", wait_for_update: 500 });
  win.gtag("js", new Date());
}

function consentUpdate(c: Consent | null) {
  if (!c || !w().gtag) return;
  w().gtag("consent", "update", {
    analytics_storage: c.analytics ? "granted" : "denied",
    ad_storage: c.marketing ? "granted" : "denied",
    ad_user_data: c.marketing ? "granted" : "denied",
    ad_personalization: c.marketing ? "granted" : "denied",
  });
}

function loadPosthog(t: PublicTracking) {
  if (!t.posthog_key || loaded.has("posthog")) return;
  loaded.add("posthog");
  const host = (t.posthog_host || "https://us.i.posthog.com").replace(/\/$/, "");
  /* PostHog's standard loader, shortened */
  const win = w();
  const ph: any = (win.posthog = win.posthog || []);
  ph._i = ph._i || [];
  ph.init = (key: string, cfg: any) => {
    ph._i.push([key, cfg, "posthog"]);
  };
  ["capture", "identify", "alias", "people.set", "register", "reset", "opt_in_capturing", "opt_out_capturing", "set_config", "group", "onFeatureFlags"].forEach((m) => {
    const parts = m.split(".");
    let target = ph;
    if (parts.length === 2) {
      target = ph[parts[0]] = ph[parts[0]] || {};
    }
    const name = parts[parts.length - 1];
    target[name] = (...args: unknown[]) => ph.push([m, ...args]);
  });
  ph.__SV = 1;
  script("rb-posthog", `${host.replace(".i.posthog.com", "-assets.i.posthog.com")}/static/array.js`);
  ph.init(t.posthog_key, {
    api_host: host,
    person_profiles: "identified_only",
    capture_pageview: false, // sent on every route change by Analytics.tsx
    capture_pageleave: true,
    autocapture: true,
    disable_session_recording: !t.posthog_session_replay,
    session_recording: { maskAllInputs: true },
    bootstrap: browserId() ? { distinctID: browserId() } : undefined,
  });
}

function loadGoogle(t: PublicTracking, c: Consent | null) {
  // GA4 always (consent mode keeps it cookieless until analytics is allowed); Ads only with marketing consent.
  const ids = [t.ga4_measurement_id, c?.marketing ? t.google_ads_id : null].filter(Boolean) as string[];
  if (!ids.length) return;
  gtagBase();
  consentUpdate(c);
  script("rb-gtag", `https://www.googletagmanager.com/gtag/js?id=${ids[0]}`);
  for (const id of ids) {
    if (loaded.has(id) || w().__rbGa === id) continue; // the page's HTML already configured GA4
    loaded.add(id);
    w().gtag("config", id, { send_page_view: false });
  }
}

function loadClarity(t: PublicTracking) {
  if (!t.clarity_project_id || loaded.has("clarity")) return;
  loaded.add("clarity");
  const win = w();
  win.clarity = win.clarity || ((...a: unknown[]) => (win.clarity.q = win.clarity.q || []).push(a));
  script("rb-clarity", `https://www.clarity.ms/tag/${t.clarity_project_id}`);
  win.clarity("consent");
}

function loadMeta(t: PublicTracking) {
  if (!t.meta_pixel_id || loaded.has("meta")) return;
  loaded.add("meta");
  const win = w();
  if (!win.fbq) {
    const n: any = (win.fbq = function fbq(...args: unknown[]) {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      n.callMethod ? n.callMethod(...args) : n.queue.push(args);
    });
    if (!win._fbq) win._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = "2.0";
    n.queue = [];
  }
  script("rb-meta", "https://connect.facebook.net/en_US/fbevents.js");
  win.fbq("init", t.meta_pixel_id);
}

function loadTikTok(t: PublicTracking) {
  if (!t.tiktok_pixel_id || loaded.has("tiktok")) return;
  loaded.add("tiktok");
  const win = w();
  const ttq: any = (win.ttq = win.ttq || []);
  ttq.methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie", "disableCookie"];
  ttq.setAndDefer = (o: any, m: string) => {
    o[m] = (...a: unknown[]) => o.push([m, ...a]);
  };
  ttq.methods.forEach((m: string) => ttq.setAndDefer(ttq, m));
  script("rb-tiktok", `https://analytics.tiktok.com/i18n/pixel/events.js?sdkid=${t.tiktok_pixel_id}&lib=ttq`);
  ttq._i = ttq._i || {};
  ttq._i[t.tiktok_pixel_id] = [];
}

function loadLinkedIn(t: PublicTracking) {
  if (!t.linkedin_partner_id || loaded.has("linkedin")) return;
  loaded.add("linkedin");
  const win = w();
  win._linkedin_partner_id = t.linkedin_partner_id;
  win._linkedin_data_partner_ids = win._linkedin_data_partner_ids || [];
  win._linkedin_data_partner_ids.push(t.linkedin_partner_id);
  win.lintrk = win.lintrk || ((...a: unknown[]) => (win.lintrk.q = win.lintrk.q || []).push(a));
  script("rb-linkedin", "https://snap.licdn.com/li.lms-analytics/insight.min.js");
}

function loadX(t: PublicTracking) {
  if (!t.x_pixel_id || loaded.has("x")) return;
  loaded.add("x");
  const win = w();
  if (!win.twq) {
    const s: any = (win.twq = (...a: unknown[]) => (s.exe ? s.exe(...a) : s.queue.push(a)));
    s.version = "1.1";
    s.queue = [];
  }
  script("rb-x", "https://static.ads-twitter.com/uwt.js");
  win.twq("config", t.x_pixel_id);
}

/** Loads whatever the visitor's consent allows. Safe to call repeatedly. */
export async function applyConsent(c: Consent | null = getConsent()) {
  const cfg = await getConfig();
  if (!cfg) return;
  const t = cfg.tracking;
  loadGoogle(t, c);
  if (!c) return;
  if (c.analytics) {
    loadPosthog(t);
    loadClarity(t);
  } else if (w().posthog?.opt_out_capturing) {
    w().posthog.opt_out_capturing();
  }
  loadGoogle(t, c);
  if (c.marketing) {
    loadMeta(t);
    loadTikTok(t);
    loadLinkedIn(t);
    loadX(t);
  }
  consentUpdate(c);
}

/** Page view to every loaded tag. */
export async function pixelsPageview() {
  const c = getConsent();
  const cfg = await getConfig();
  if (!cfg) return;
  const t = cfg.tracking;
  const win = w();
  const path = window.location.pathname + window.location.search;
  // GA4 counts every page view: cookieless and anonymous until analytics is allowed (consent mode).
  if (win.gtag && t.ga4_measurement_id) {
    win.gtag("event", "page_view", { send_to: t.ga4_measurement_id, page_location: window.location.href, page_path: path, page_title: document.title });
  }
  if (!c) return;
  if (c.analytics && win.posthog?.capture) win.posthog.capture("$pageview");
  if (win.gtag && c.marketing && t.google_ads_id) win.gtag("event", "page_view", { send_to: t.google_ads_id });
  if (c.marketing) {
    win.fbq?.("track", "PageView");
    win.ttq?.page?.();
  }
}

/** A named event to every loaded tag. Event ids match the server's. */
export async function pixelsEvent(name: string, props: Record<string, unknown>) {
  const c = getConsent();
  if (!c) return;
  const cfg = await getConfig();
  if (!cfg) return;
  const t = cfg.tracking;
  const win = w();
  if (c.analytics) win.posthog?.capture?.(name, props);
  const value = typeof props.value === "number" ? props.value : undefined;
  const currency = (props.currency as string) || "USD";
  if (name === "signup") {
    const eventID = props.user_id ? `signup-${props.user_id}` : undefined;
    if (c.analytics && t.ga4_measurement_id) win.gtag?.("event", "sign_up", { send_to: t.ga4_measurement_id, method: props.method || "email" });
    if (c.marketing) {
      win.fbq?.("track", "CompleteRegistration", {}, eventID ? { eventID } : undefined);
      if (t.google_ads_id && t.google_ads_signup_label) win.gtag?.("event", "conversion", { send_to: `${t.google_ads_id}/${t.google_ads_signup_label}` });
      win.ttq?.track?.("CompleteRegistration", {}, eventID ? { event_id: eventID } : undefined);
      if (t.linkedin_signup_conversion_id) win.lintrk?.("track", { conversion_id: Number(t.linkedin_signup_conversion_id) });
      if (t.x_signup_event_id) win.twq?.("event", t.x_signup_event_id, {});
    }
  } else if (name === "checkout_started") {
    if (c.analytics && t.ga4_measurement_id) win.gtag?.("event", "begin_checkout", { send_to: t.ga4_measurement_id, currency, value, items: [{ item_name: props.plan }] });
    if (c.marketing) {
      win.fbq?.("track", "InitiateCheckout", { value, currency, content_name: props.plan });
      win.ttq?.track?.("InitiateCheckout", { value, currency });
    }
  } else if (name === "purchase") {
    const eventID = props.transaction_id ? `txn-${props.transaction_id}` : undefined;
    if (c.analytics && t.ga4_measurement_id) win.gtag?.("event", "purchase", { send_to: t.ga4_measurement_id, transaction_id: eventID, value, currency });
    if (c.marketing) {
      win.fbq?.("track", "Subscribe", { value, currency, predicted_ltv: value }, eventID ? { eventID } : undefined);
      if (t.google_ads_id && t.google_ads_purchase_label)
        win.gtag?.("event", "conversion", { send_to: `${t.google_ads_id}/${t.google_ads_purchase_label}`, value, currency, transaction_id: eventID });
      win.ttq?.track?.("CompletePayment", { value, currency }, eventID ? { event_id: eventID } : undefined);
    }
  }
}

/** Joins PostHog's anonymous visitor to the signed-in account (id only, never the email). */
let identifiedAs: string | null = null;

export function pixelsIdentify(userId: string | null) {
  const win = w();
  if (!getConsent()?.analytics || !win.posthog?.identify) return;
  if (userId && userId !== identifiedAs) {
    win.posthog.identify(userId);
    identifiedAs = userId;
  } else if (!userId && identifiedAs) {
    win.posthog.reset?.(); // signed out: the next person on this browser starts fresh
    identifiedAs = null;
  }
}
